import React, {
  ReactElement,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { Spacing, content } from "@glossy";
import { useFullscreen } from "@lkf/hooks";
import Button from "@ui-kit/button";
import Menu from "@ui-kit/popover/menu";
import Tile from "@radarly/components/cards/card";
import ErrorCardInner from "@radarly/components/error-boundary/card-inner";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import {
  isAICardDataviz,
  isTextCardDataviz,
} from "@radarly/models/cards/dataviz";
import { getCardPNGID } from "@radarly/models/cards/png";
import { Clause } from "@radarly/models/clause";
import { Dataviz, isKpiDataviz } from "@radarly/models/dataviz";
import ScopeAction from "@radarly/shared/cards/actions/scope";
import { ErrorBoundary } from "@radarly/shared/error-boundary";
import { useGeneratePNG } from "../hooks/useGeneratePNG";
import FullscreenTile from "./fullscreen";
import ReloadOverlay from "./reload-overlay";
import { useExplainer } from "./useExplainer";

interface IProps {
  title: string;
  height?: number;
  subtitle?: ReactNode;
  isAI?: boolean;
  isPNG?: boolean;
  statusInfo?: ReactElement;
  children: ReactElement;
  clause?: Clause;
  actions?: ReactNode;
  hideScope?: boolean;
  needReload?: boolean;
  onClickReload?: () => void;
  onGenerateCSV?: () => void;
}

type TileAction = {
  id: "fullscreen" | "csv" | "png";
  label: string;
};

export const WrapperScopeActions = styled.div<{ selected: boolean }>`
  visibility: ${(props) => (props.selected ? "visible !important" : "hidden")};
  display: flex;
  align-items: center;
  color: ${(props) => content(props, "secondary")};
  gap: ${Spacing.Micro};
`;

const Wrapper = styled.div`
  position: relative;
  :hover {
    .wrapper-scope-actions {
      visibility: visible;
    }
  }
`;

function isFullscreenEnabled(dataviz: Dataviz): boolean {
  const hash = window.location.hash ?? "";
  if (hash.includes("pages/wizard") || hash.includes("pages/editor")) {
    return false;
  }
  if (dataviz) {
    if (isKpiDataviz(dataviz)) {
      return dataviz.displayDistributions;
    }
    if (isAICardDataviz(dataviz)) {
      return false;
    }
  }
  return true;
}

function isExportEnabled(): boolean {
  const hash = window.location.hash ?? "";
  return !hash.includes("pages/wizard") && !hash.includes("pages/editor");
}

function shouldDisplayScope(dataviz: Dataviz): boolean {
  if (dataviz) {
    return !isTextCardDataviz(dataviz);
  }
  return true;
}

const RadarlyTileCard = ({
  title,
  subtitle,
  statusInfo,
  isAI,
  isPNG,
  children,
  height = 330,
  clause,
  actions,
  hideScope,
  onClickReload,
  onGenerateCSV,
  needReload,
}: IProps) => {
  const locale = useCurrentUserLocale();
  const exportEnabled = isExportEnabled();
  const isCSVExportEnabled = !!onGenerateCSV && exportEnabled;
  const isPNGExportEnabled = isPNG && exportEnabled;
  const fullscreenRef = useRef(null);
  const fullscreenEnabled = isFullscreenEnabled(children?.props?.dataviz);
  const displayScope =
    !hideScope && shouldDisplayScope(children?.props?.dataviz);
  const { isFullscreen, openFullscreen, closeFullscreen } =
    useFullscreen(fullscreenRef);
  const tileID = getCardPNGID(title, children?.["_owner"]?._debugID);
  const { cardPNG, onGeneratePNG } = useGeneratePNG(
    tileID,
    title,
    height,
    locale
  );
  const [showActions, setShowActions] = useState<boolean>(false);
  const { magicFooter, handleMouseOver, explainerRef } = useExplainer(locale);
  const showDropdown =
    fullscreenEnabled || isCSVExportEnabled || isPNGExportEnabled;

  const dropdownActions: TileAction[] = useMemo(() => {
    const actions = [];
    if (fullscreenEnabled) {
      actions.push({
        id: "fullscreen",
        label: tr(locale, "INSIGHT_PAGE::ACTION::FULLSCREN"),
      });
    }
    if (fullscreenEnabled && (isCSVExportEnabled || isPNGExportEnabled))
      actions.push({ id: "separator" });
    if (isCSVExportEnabled) {
      actions.push({
        id: "csv",
        label: tr(locale, "INSIGHT_PAGE::ACTION::EXPORT_TO_CSV"),
      });
    }
    if (isPNGExportEnabled) {
      actions.push({
        id: "png",
        label: tr(locale, "INSIGHT_PAGE::ACTION::EXPORT_TO_PNG"),
      });
    }
    return actions;
  }, [fullscreenEnabled, isCSVExportEnabled, isPNGExportEnabled, locale]);

  const onClickAction = (action: TileAction["id"]) => {
    if (action === "fullscreen") {
      return openFullscreen();
    }
    if (action === "csv") {
      return onGenerateCSV();
    }
    if (action === "png") {
      return onGeneratePNG(children, subtitle);
    }
    return;
  };

  return (
    <Wrapper
      ref={explainerRef}
      onMouseOver={(e) => handleMouseOver(e)}
      id={tileID}
    >
      {cardPNG}
      {fullscreenEnabled && (
        <FullscreenTile
          ref={fullscreenRef}
          isFullscreen={isFullscreen}
          title={title}
          closeFullscreen={closeFullscreen}
          subtitle={subtitle}
          magicFooter={magicFooter}
        >
          {children}
        </FullscreenTile>
      )}
      <Tile
        title={title}
        height={height}
        subtitle={subtitle}
        magicFooter={magicFooter}
        isAI={isAI}
        statusInfo={statusInfo}
        actions={
          actions ? (
            actions
          ) : (
            <WrapperScopeActions
              className="wrapper-scope-actions"
              selected={showActions}
            >
              {displayScope && (
                <ScopeAction
                  clause={clause}
                  onOpenChange={setShowActions}
                  side="top"
                />
              )}
              {showDropdown && (
                <Menu
                  onToggle={(e) => setShowActions(e)}
                  values={dropdownActions}
                  onSelect={onClickAction}
                  disclosure={
                    <Button icon="dots-3-v" size="xs" variant="tertiary" />
                  }
                />
              )}
            </WrapperScopeActions>
          )
        }
      >
        <ErrorBoundary fallback={<ErrorCardInner locale={locale} />}>
          {children}
        </ErrorBoundary>
        <ReloadOverlay
          locale={locale}
          overflowTitleCard
          needReload={needReload}
          onClickReload={onClickReload}
        />
      </Tile>
    </Wrapper>
  );
};

export default RadarlyTileCard;
